// UserSettings.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useAuthStore from '../stores/authStore';
import useNotificationStore from '../stores/notificationStore';
import "./usersettings.css";

const API_URL = import.meta.env.VITE_API_URL || "";

const UserSettings = () => {
  const queryClient = useQueryClient();
  const { userName, updateProfile } = useAuthStore();
  const { showNotification } = useNotificationStore();

  // プロフィール（住所は3分割で管理）
  const [profile, setProfile] = useState({
    user_name: userName || "",
    user_email: "",
    phone: "",
    postcode: "",
    address: "",      // 住所（郵便番号検索で自動補完 → 手動編集可）
  });

  // 郵便番号検索
  const [postalLoading, setPostalLoading] = useState(false);
  const [postalError,   setPostalError]   = useState('');

  // パスワード
  const [pwd, setPwd] = useState({ current: "", new_password: "", confirm: "" });
  const [showPw, setShowPw] = useState({
    current: false,
    new_password: false,
    confirm: false,
  });

  // --------------------------------------------------------
  // データ取得
  // --------------------------------------------------------
  const { data: userData, isLoading: pfLoading } = useQuery({
    queryKey: ['userSettings'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/components/usersettings/userdata/`);
      if (!res.data?.success) throw new Error(res.data?.error || "failed");
      const raw = res.data.data;
      const row = Array.isArray(raw) ? raw[0] : raw;
      return row || null;
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  // データ取得後にローカル state を初期化
  // address（DB保存値）はそのまま addressDetail に入れる。
  // 郵便番号検索で上書きすることを想定。
  useEffect(() => {
    if (!userData) return;
    setProfile({
      user_name:  userData.user_name  || userName || "",
      user_email: userData.user_email || "",
      phone:      userData.phone      || "",
      postcode:   userData.postcode   || "",
      address:    userData.address    || "",
    });
  }, [userData]);

  // --------------------------------------------------------
  // 郵便番号 → 住所検索
  // --------------------------------------------------------
  const handlePostalCodeChange = (e) => {
    const raw = e.target.value.replace(/[^\d]/g, '').slice(0, 7);
    setProfile((prev) => ({ ...prev, postcode: raw }));
    setPostalError('');
  };

  const handlePostalSearch = async () => {
    const code = profile.postcode.trim();
    if (code.length !== 7) {
      setPostalError('7桁の郵便番号を入力してください。');
      return;
    }

    setPostalLoading(true);
    setPostalError('');
    try {
      const res = await axios.get(`${API_URL}/api/utility/postal/search/`, {
        params: { zipcode: code },
      });
      const result = res.data?.results?.[0];
      if (result) {
        // 都道府県 + 市区町村 + 町名 を結合して address に自動補完（そのまま編集可能）
        const autoAddress = (result.address1 || '') + (result.address2 || '') + (result.address3 || '');
        setProfile((prev) => ({ ...prev, address: autoAddress }));
      } else {
        const msg = res.data?.message || '該当する住所が見つかりませんでした。';
        setPostalError(msg);
        showNotification(msg, 'error');
      }
    } catch {
      const msg = '住所の取得に失敗しました。';
      setPostalError(msg);
      showNotification(msg, 'error');
    } finally {
      setPostalLoading(false);
    }
  };

  // --------------------------------------------------------
  // プロフィール保存
  // --------------------------------------------------------
  const profileMutation = useMutation({
    mutationFn: async (profileData) => {
      const payload = {
        profile: {
          user_name:  profileData.user_name,
          user_email: profileData.user_email,
          phone:      profileData.phone,
          postcode:   profileData.postcode,
          address:    profileData.address,
        },
      };
      const res = await axios.put(`${API_URL}/api/components/usersettings/saveProfile/`, payload);
      if (!res.data?.success) {
        throw new Error(res.data?.message || "プロフィールの保存に失敗しました");
      }
      return res.data;
    },
    onSuccess: () => {
      updateProfile({ userName: profile.user_name });
      queryClient.invalidateQueries({ queryKey: ['userSettings'] });
      showNotification('プロフィールを保存しました');
    },
    onError: (err) => {
      showNotification(err.message || "プロフィールの保存に失敗しました", "error");
    },
  });

  // --------------------------------------------------------
  // パスワード変更
  // --------------------------------------------------------
  const passwordMutation = useMutation({
    mutationFn: async (pwdData) => {
      const res = await axios.post(
        `${API_URL}/api/components/usersettings/changePassword/`,
        pwdData
      );
      if (!res.data?.success) {
        throw new Error(res.data?.message || "パスワードの変更に失敗しました");
      }
      return res.data;
    },
    onSuccess: () => {
      showNotification('パスワードを変更しました');
      setPwd({ current: "", new_password: "", confirm: "" });
    },
    onError: (err) => {
      showNotification(err.message || "パスワードの変更に失敗しました", "error");
    },
  });

  // --------------------------------------------------------
  // ハンドラ
  // --------------------------------------------------------
  const handleProfileChange = (field) => (e) => {
    setProfile((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const saveProfile = () => {
    if (!profile.user_name.trim()) {
      showNotification('表示名を入力してください。', 'error');
      return;
    }
    if (!profile.user_email.trim()) {
      showNotification('メールアドレスを入力してください。', 'error');
      return;
    }
    if (!profile.postcode.trim()) {
      showNotification('郵便番号を入力してください。', 'error');
      return;
    }
    if (!profile.address.trim()) {
      showNotification('住所を入力してください。', 'error');
      return;
    }
    profileMutation.mutate(profile);
  };

  const changePassword = () => {
    if (!pwd.new_password || !pwd.confirm) {
      showNotification("新しいパスワードを入力してください", "error");
      return;
    }
    if (pwd.new_password !== pwd.confirm) {
      showNotification("新しいパスワードが一致しません", "error");
      return;
    }
    if (pwd.new_password.length < 8) {
      showNotification("新しいパスワードは8文字以上にしてください", "error");
      return;
    }
    passwordMutation.mutate(pwd);
  };

  // --------------------------------------------------------
  // レンダリング
  // --------------------------------------------------------
  return (
    <div className="h01-wrap">
      <h2 className="h01-page-title">個人設定</h2>

      {/* ── プロフィール ── */}
      <section className="h01-section">
        <div className="h01-sec__hd">
          <h3>プロフィール</h3>
        </div>

        {pfLoading ? (
          <div className="h01-loading">読み込み中…</div>
        ) : (
          <div className="h01-form">

            {/* 表示名 */}
            <div className="h01-field">
              <label htmlFor="pf-name">
                表示名 <span className="h01-badge h01-badge--required">必須</span>
              </label>
              <input
                id="pf-name"
                type="text"
                value={profile.user_name}
                onChange={handleProfileChange('user_name')}
                required
              />
            </div>

            {/* メールアドレス */}
            <div className="h01-field">
              <label htmlFor="pf-mail">
                メールアドレス <span className="h01-badge h01-badge--required">必須</span>
              </label>
              <input
                id="pf-mail"
                type="email"
                value={profile.user_email}
                onChange={handleProfileChange('user_email')}
                required
              />
            </div>

            {/* 電話番号（任意） */}
            <div className="h01-field">
              <label htmlFor="pf-phone">
                電話番号 <span className="h01-badge h01-badge--optional">任意</span>
              </label>
              <input
                id="pf-phone"
                type="tel"
                value={profile.phone}
                onChange={handleProfileChange('phone')}
                placeholder="090-0000-0000"
              />
            </div>

            {/* 郵便番号 */}
            <div className="h01-field">
              <label htmlFor="pf-postcode">
                郵便番号 <span className="h01-badge h01-badge--required">必須</span>
              </label>
              <div className="h01-postal-row">
                <span className="h01-postal-prefix">〒</span>
                <input
                  id="pf-postcode"
                  type="text"
                  inputMode="numeric"
                  value={profile.postcode}
                  onChange={handlePostalCodeChange}
                  placeholder="1234567"
                  maxLength={7}
                  autoComplete="postal-code"
                  className="h01-postal-input"
                />
                <button
                  type="button"
                  className="h01-btn h01-btn--search"
                  onClick={handlePostalSearch}
                  disabled={postalLoading || profile.postcode.length !== 7}
                >
                  {postalLoading ? '検索中…' : '住所を検索'}
                </button>
              </div>
              {postalError && <span className="h01-field-error">{postalError}</span>}
              <span className="h01-field-hint">ハイフンなし7桁で入力してください</span>
            </div>

            {/* 住所 */}
            <div className="h01-field">
              <label htmlFor="pf-address">
                住所 <span className="h01-badge h01-badge--required">必須</span>
              </label>
              <input
                id="pf-address"
                type="text"
                value={profile.address}
                onChange={handleProfileChange('address')}
                placeholder="東京都渋谷区渋谷1-2-3"
                autoComplete="street-address"
                required
              />
              <span className="h01-field-hint">郵便番号検索後に番地・建物名を追記してください</span>
            </div>

            <div className="h01-actions">
              <button
                className="h01-btn h01-btn--primary"
                onClick={saveProfile}
                disabled={profileMutation.isPending}
              >
                {profileMutation.isPending ? "保存中…" : "保存"}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ── パスワード変更 ── */}
      <section className="h01-section">
        <div className="h01-sec__hd">
          <h3>パスワード変更</h3>
        </div>
        <div className="h01-form">

          <div className="h01-field h01-field--pw">
            <label htmlFor="pw-cur">現在のパスワード</label>
            <div className="h01-pw-input">
              <input
                id="pw-cur"
                name="cur_pw_input"
                type={showPw.current ? "text" : "password"}
                value={pwd.current}
                onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                autoComplete="off"
                readOnly
                onFocus={(e) => e.currentTarget.removeAttribute("readOnly")}
              />
              <button
                type="button"
                className="h01-pw-toggle"
                onClick={() => setShowPw((s) => ({ ...s, current: !s.current }))}
                aria-label={showPw.current ? "パスワードを非表示" : "パスワードを表示"}
              >
                {showPw.current ? "隠す" : "表示"}
              </button>
            </div>
          </div>

          <div className="h01-field h01-field--pw">
            <label htmlFor="pw-new">新しいパスワード</label>
            <div className="h01-pw-input">
              <input
                id="pw-new"
                type={showPw.new_password ? "text" : "password"}
                value={pwd.new_password}
                onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="h01-pw-toggle"
                onClick={() => setShowPw((s) => ({ ...s, new_password: !s.new_password }))}
                aria-label={showPw.new_password ? "パスワードを非表示" : "パスワードを表示"}
              >
                {showPw.new_password ? "隠す" : "表示"}
              </button>
            </div>
          </div>

          <div className="h01-field h01-field--pw">
            <label htmlFor="pw-cf">新しいパスワード（確認）</label>
            <div className="h01-pw-input">
              <input
                id="pw-cf"
                type={showPw.confirm ? "text" : "password"}
                value={pwd.confirm}
                onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="h01-pw-toggle"
                onClick={() => setShowPw((s) => ({ ...s, confirm: !s.confirm }))}
                aria-label={showPw.confirm ? "パスワードを非表示" : "パスワードを表示"}
              >
                {showPw.confirm ? "隠す" : "表示"}
              </button>
            </div>
          </div>

          <div className="h01-actions">
            <button
              className="h01-btn"
              onClick={changePassword}
              disabled={passwordMutation.isPending}
            >
              {passwordMutation.isPending ? "変更中…" : "変更する"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UserSettings;